import { Box, Stack, Tab, Tabs } from '@mui/material';

import { RefObject, useCallback, useMemo, useState } from 'react';
import { INDUSTRY_STANDARD_MATCHES_TAB } from '../../constants';
import { StandardLibraryCompetency } from '../../interfaces/Competency';
import { IJobItem } from '../../interfaces/Job';
import { ITask } from '../../interfaces/Task';
import { useLocale } from '../../providers/LocaleProvider';
import StandardMatchedJobs from './StandardMatchedJobs';
import StandardMatchedTasksAndCompetencies, {
  StandardMatchedTasksAndCompetenciesRef,
} from './StandardMatchedTasksAndCompetencies';

export type IndustryStandardMatchesProps = {
  theme: any;
  jobs: IJobItem[];
  tasks: ITask[];
  competencies: Omit<StandardLibraryCompetency, 'hasTraining'>[];
  onClose?: () => void;
  hideActions?: boolean;
  activeTab?: INDUSTRY_STANDARD_MATCHES_TAB;
  onTabChange?: (tab: INDUSTRY_STANDARD_MATCHES_TAB) => void;
  onJobSelectionChange?: (jobId: string | null) => void;
  onTasksSelectionChange?: (
    hasSelection: boolean,
    taskCount: number,
    competencyCount: number
  ) => void;
  tasksRef?: RefObject<StandardMatchedTasksAndCompetenciesRef | null>;
  /** Changing this remounts the tasks/competencies tables (used to recover the MUI DataGrid layout
   *  after the host hid them with display:none). Selection is controlled, so it survives. */
  tableNonce?: number;
};

const IndustryStandardMatches = ({
  theme,
  jobs,
  competencies,
  tasks,
  onClose,
  hideActions,
  activeTab: controlledTab,
  onTabChange,
  onJobSelectionChange,
  onTasksSelectionChange,
  tasksRef,
  tableNonce,
}: IndustryStandardMatchesProps) => {
  const [internalTab, setInternalTab] = useState(INDUSTRY_STANDARD_MATCHES_TAB.JOBS);
  const { translate } = useLocale();

  const activeTab = onTabChange
    ? (controlledTab ?? INDUSTRY_STANDARD_MATCHES_TAB.JOBS)
    : internalTab;

  const handleTabChange = (_: any, value: INDUSTRY_STANDARD_MATCHES_TAB) => {
    if (onTabChange) {
      onTabChange(value);
    } else {
      setInternalTab(value);
    }
  };

  const isJobsTab = useMemo(() => activeTab === INDUSTRY_STANDARD_MATCHES_TAB.JOBS, [activeTab]);

  const renderTabContent = useCallback(() => {
    if (isJobsTab) {
      return (
        <StandardMatchedJobs
          theme={theme}
          jobs={jobs}
          onClose={onClose}
          hideActions={hideActions}
          onSelectionChange={onJobSelectionChange}
        />
      );
    }

    return (
      <StandardMatchedTasksAndCompetencies
        ref={tasksRef}
        theme={theme}
        tasks={tasks}
        competencies={competencies}
        onClose={onClose}
        hideActions={hideActions}
        onSelectionChange={onTasksSelectionChange}
        tableNonce={tableNonce}
      />
    );
  }, [
    theme,
    jobs,
    tasks,
    competencies,
    isJobsTab,
    onClose,
    onJobSelectionChange,
    onTasksSelectionChange,
    tasksRef,
    tableNonce,
  ]);

  return (
    <Stack display='flex' flexDirection='column' gap={2} width={'100%'}>
      <div>{translate('standardsLibrary.weFoundMatches')}</div>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          centered={true}
          value={activeTab}
          variant='fullWidth'
          sx={{ width: '100%' }}
          onChange={handleTabChange}
        >
          <Tab
            label={translate('standardsLibrary.matchedJobs')}
            value={INDUSTRY_STANDARD_MATCHES_TAB.JOBS}
          />
          <Tab
            label={translate('standardsLibrary.tasksAndCompetenciesTabLabel')}
            value={INDUSTRY_STANDARD_MATCHES_TAB.TASKS_AND_COMPETENCIES}
          />
        </Tabs>
      </Box>
      {renderTabContent()}
    </Stack>
  );
};

export default IndustryStandardMatches;
